import { Text } from '@cinch-labs/design-system'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import styles from './finance-representative-example.module.css'
import { interpolateI18n } from '@cinch-labs/shared-util'

const interpolateText = (template: string) =>
  interpolateI18n(
    template,
    {},
    {
      b: (text: string) => (
        <Text element="span" fontWeight="bold" fontSizeFixed="xs" key={text}>
          {text}
        </Text>
      ),
    },
  )

interface FinanceRepresentativeExampleProps {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  minScreenWidthForTable?: number
}

export const FinanceRepresentativeExample = ({
  headingLevel = 'h3',
  minScreenWidthForTable = 0,
}: FinanceRepresentativeExampleProps) => {
  const { isDesktop } = useResponsiveEnvironment()

  const displayTable = isDesktop && window.innerWidth > minScreenWidthForTable
  const text = interpolateText(
    displayTable
      ? `Based on an on the road price of {b}£31,723.92{/b}, with a {b}£2,800.00{/b} customer deposit, plus a {b}£1,060.00{/b} finance deposit contribution and borrowing {b}£27,863.92{/b} at a representative APR of {b}3.5%{/b} and the following:`
      : `Based on an on the road price of £31,723.92, with a £2,800.00 customer deposit, plus a £1,060.00 finance deposit contribution and borrowing £27,863.92 at a representative APR of 3.5%. 49 monthly payments of £325.00. Fixed rate of interest per annum 1.8%. Optional final payment £15,318.00. Amount of interest £3,054.08. Total amount payable £34,778.00. Annual mileage limit 6,000 miles. Excess mileage charge 14.9p per mile. On the road price includes a retailer discount of £1,591.08.`,
  )

  const dataFields = [
    {
      term: '49 monthly payments of',
      details: '£325.00',
    },
    {
      term: 'Fixed rate of interest per annum',
      details: '1.8%',
    },
    {
      term: 'Optional final payment',
      details: '£15,318.00',
    },
    {
      term: 'Amount of interest',
      details: '£3,054.08',
    },
    {
      term: 'Total amount payable',
      details: '£34,778.00',
    },
    {
      term: 'Annual mileage limit',
      details: '6,000 miles',
    },
    {
      term: 'Excess mileage charge',
      details: '14.9p per mile',
    },
    {
      term: 'Retailer discount',
      details: '£1,591.08',
    },
  ]

  return (
    <section className={styles.container}>
      <Text
        element={headingLevel}
        fontWeight="bold"
        fontSize="s"
        className={styles.title}
      >
        Personal Contract Purchase (PCP) representative example
      </Text>

      <Text fontWeight="book" fontSizeFixed="xs">
        {text}
      </Text>

      {displayTable && (
        <table className={styles.table}>
          <tbody>
            <tr className={styles.row}>
              {dataFields.map((item: { term: string; details: string }) => (
                <td key={item.term}>
                  <Text
                    fontSizeFixed="xs"
                    fontWeight="book"
                    className={styles.columnHeader}
                  >
                    {item.term}
                  </Text>
                  <Text fontSizeFixed="xs" fontWeight="bold">
                    {item.details}
                  </Text>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </section>
  )
}
